// BACKEND INTEGRATION POINT: Replace this entire module with real API calls
// All functions here simulate server-side logic that must be replicated in a real backend

export type ServiceStatus = 'active' | 'inactive';
export type BookingStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';

export interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  duration: number; // minutes
  status: ServiceStatus;
  category: string;
}

export interface BusinessHours {
  day: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  dayName: string;
  isOpen: boolean;
  openTime: string; // "08:00"
  closeTime: string; // "18:00"
}

export interface BlockedPeriod {
  id: string;
  date: string; // "YYYY-MM-DD"
  startTime: string | null; // null = full day
  endTime: string | null;
  reason: string;
  createdAt: string;
}

export interface Booking {
  id: string;
  customerName: string;
  customerWhatsapp: string;
  customerNotes: string;
  serviceIds: string[];
  date: string; // "YYYY-MM-DD"
  startTime: string; // "HH:MM"
  endTime: string; // "HH:MM"
  totalPrice: number;
  totalDuration: number; // minutes
  status: BookingStatus;
  createdAt: string;
}

export interface BookingSettings {
  slotInterval: number; // minutes: 15, 30, 60
  breakBetweenAppointments: number; // minutes
  timezone: string;
}

// ─── Mock State (replace with DB) ─────────────────────────────────────────────

export const mockServices: Service[] = [
  { id: 'svc-001', name: 'Axila', description: 'Depilação completa da axila com cera quente.', price: 300, duration: 30, status: 'active', category: 'Depilação' },
  { id: 'svc-002', name: 'Meia Perna', description: 'Depilação da meia perna inferior com cera.', price: 400, duration: 45, status: 'active', category: 'Depilação' },
  { id: 'svc-003', name: 'Virilha Simples', description: 'Depilação da virilha simples com cera quente.', price: 550, duration: 40, status: 'active', category: 'Depilação' },
  { id: 'svc-004', name: 'Virilha Completa', description: 'Depilação completa da virilha com cera quente.', price: 700, duration: 60, status: 'active', category: 'Depilação' },
  { id: 'svc-005', name: 'Perna Inteira', description: 'Depilação da perna completa com cera.', price: 650, duration: 60, status: 'active', category: 'Depilação' },
  { id: 'svc-006', name: 'Buço', description: 'Remoção suave dos pelos do buço com cera fria.', price: 200, duration: 15, status: 'active', category: 'Depilação' },
  { id: 'svc-007', name: 'Queixo', description: 'Depilação delicada da região do queixo com cera.', price: 250, duration: 15, status: 'active', category: 'Depilação' },
  { id: 'svc-008', name: 'Barriga', description: 'Depilação da região abdominal com cera quente premium.', price: 400, duration: 30, status: 'active', category: 'Depilação' },
  { id: 'svc-009', name: 'Braço Inteiro', description: 'Depilação completa do braço do ombro ao pulso.', price: 550, duration: 45, status: 'active', category: 'Depilação' },
  { id: 'svc-010', name: 'Meio Braço', description: 'Depilação da metade inferior do braço com cera.', price: 350, duration: 30, status: 'active', category: 'Depilação' },
];

export const mockBusinessHours: BusinessHours[] = [
  { day: 0, dayName: 'Domingo', isOpen: false, openTime: '08:00', closeTime: '18:00' },
  { day: 1, dayName: 'Segunda', isOpen: true, openTime: '08:00', closeTime: '18:00' },
  { day: 2, dayName: 'Terça', isOpen: true, openTime: '08:00', closeTime: '18:00' },
  { day: 3, dayName: 'Quarta', isOpen: true, openTime: '08:00', closeTime: '18:00' },
  { day: 4, dayName: 'Quinta', isOpen: true, openTime: '08:00', closeTime: '18:00' },
  { day: 5, dayName: 'Sexta', isOpen: true, openTime: '08:00', closeTime: '18:00' },
  { day: 6, dayName: 'Sábado', isOpen: true, openTime: '09:00', closeTime: '16:00' },
];

export const mockBlockedPeriods: BlockedPeriod[] = [
  { id: 'blk-001', date: '2026-09-16', startTime: '12:00', endTime: '13:00', reason: 'Almoço', createdAt: '2026-09-10T08:00:00' },
  { id: 'blk-002', date: '2026-09-17', startTime: null, endTime: null, reason: 'Feriado Nacional', createdAt: '2026-09-10T08:00:00' },
  { id: 'blk-003', date: '2026-09-22', startTime: '08:00', endTime: '11:00', reason: 'Manutenção', createdAt: '2026-09-12T10:00:00' },
];

export const mockBookings: Booking[] = [];

export const mockSettings: BookingSettings = {
  slotInterval: 30,
  breakBetweenAppointments: 0,
  timezone: 'Africa/Maputo',
};

// ─── In-memory mutable state ──────────────────────────────────────────────────
let _bookings: Booking[] = [...mockBookings];
let _businessHours: BusinessHours[] = [...mockBusinessHours];
let _blockedPeriods: BlockedPeriod[] = [...mockBlockedPeriods];
let _services: Service[] = [...mockServices];
let _settings: BookingSettings = { ...mockSettings };

// ─── Utility functions ────────────────────────────────────────────────────────

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function addMinutes(time: string, mins: number): string {
  return minutesToTime(timeToMinutes(time) + mins);
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function getServices(): Service[] {
  return _services;
}

export function getActiveServices(): Service[] {
  return _services.filter(s => s.status === 'active');
}

export function getBookings(): Booking[] {
  return _bookings;
}

export function getBusinessHours(): BusinessHours[] {
  return _businessHours;
}

export function getBlockedPeriods(): BlockedPeriod[] {
  return _blockedPeriods;
}

export function getSettings(): BookingSettings {
  return _settings;
}

export function updateSettings(settings: Partial<BookingSettings>): void {
  _settings = { ..._settings, ...settings };
}

export function updateBusinessHours(hours: BusinessHours[]): void {
  _businessHours = hours;
}

export function updateService(serviceId: string, updates: Partial<Service>): void {
  _services = _services.map(s => s.id === serviceId ? { ...s, ...updates } : s);
}

export function addService(service: Omit<Service, 'id'>): Service {
  const newService: Service = { ...service, id: `svc-${Date.now()}` };
  _services = [..._services, newService];
  return newService;
}

export function addBlockedPeriod(period: Omit<BlockedPeriod, 'id' | 'createdAt'>): BlockedPeriod {
  const newBlock: BlockedPeriod = {
    ...period,
    id: `blk-${Date.now()}`,
    createdAt: new Date().toISOString(),
  };
  _blockedPeriods = [..._blockedPeriods, newBlock];
  return newBlock;
}

export function removeBlockedPeriod(id: string): void {
  _blockedPeriods = _blockedPeriods.filter(b => b.id !== id);
}

export function isDayAvailable(dateStr: string): boolean {
  const date = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = date.getDay();
  const hours = _businessHours.find(h => h.day === dayOfWeek);
  if (!hours || !hours.isOpen) return false;

  const fullDayBlock = _blockedPeriods.find(
    b => b.date === dateStr && b.startTime === null
  );
  if (fullDayBlock) return false;

  return true;
}

export function getAvailableSlots(dateStr: string, totalDuration: number): string[] {
  const date = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = date.getDay();
  const hours = _businessHours.find(h => h.day === dayOfWeek);
  if (!hours || !hours.isOpen) return [];

  const fullDayBlock = _blockedPeriods.find(
    b => b.date === dateStr && b.startTime === null
  );
  if (fullDayBlock) return [];

  const openMin = timeToMinutes(hours.openTime);
  const closeMin = timeToMinutes(hours.closeTime);
  const interval = _settings.slotInterval;
  const breakTime = _settings.breakBetweenAppointments;

  const dayBookings = _bookings
    .filter(b => b.date === dateStr && b.status !== 'cancelled')
    .map(b => ({ start: timeToMinutes(b.startTime), end: timeToMinutes(b.endTime) }));

  const dayBlocks = _blockedPeriods
    .filter(b => b.date === dateStr && b.startTime !== null)
    .map(b => ({ start: timeToMinutes(b.startTime!), end: timeToMinutes(b.endTime!) }));

  const occupied = [...dayBookings, ...dayBlocks];

  const slots: string[] = [];

  for (let startMin = openMin; startMin + totalDuration <= closeMin; startMin += interval) {
    const endMin = startMin + totalDuration + breakTime;
    const overlaps = occupied.some(occ => startMin < occ.end && endMin > occ.start);
    if (!overlaps) {
      slots.push(minutesToTime(startMin));
    }
  }

  return slots;
}

export interface SlotInfo {
  time: string;
  available: boolean;
}

export function getAllSlots(dateStr: string, totalDuration: number): SlotInfo[] {
  const date = new Date(dateStr + 'T12:00:00');
  const dayOfWeek = date.getDay();
  const hours = _businessHours.find(h => h.day === dayOfWeek);
  if (!hours || !hours.isOpen) return [];

  const fullDayBlock = _blockedPeriods.find(
    b => b.date === dateStr && b.startTime === null
  );
  if (fullDayBlock) return [];

  const openMin = timeToMinutes(hours.openTime);
  const closeMin = timeToMinutes(hours.closeTime);
  const interval = _settings.slotInterval;
  const breakTime = _settings.breakBetweenAppointments;

  const dayBookings = _bookings
    .filter(b => b.date === dateStr && b.status !== 'cancelled')
    .map(b => ({ start: timeToMinutes(b.startTime), end: timeToMinutes(b.endTime) }));

  const dayBlocks = _blockedPeriods
    .filter(b => b.date === dateStr && b.startTime !== null)
    .map(b => ({ start: timeToMinutes(b.startTime!), end: timeToMinutes(b.endTime!) }));

  const occupied = [...dayBookings, ...dayBlocks];

  const slots: SlotInfo[] = [];

  for (let startMin = openMin; startMin < closeMin; startMin += interval) {
    const endMin = startMin + (totalDuration > 0 ? totalDuration : interval) + breakTime;
    const overlaps = occupied.some(occ => startMin < occ.end && endMin > occ.start);
    const fitsInDay = startMin + (totalDuration > 0 ? totalDuration : interval) <= closeMin;
    slots.push({
      time: minutesToTime(startMin),
      available: fitsInDay && !overlaps,
    });
  }

  return slots;
}

export interface CreateBookingPayload {
  customerName: string;
  customerWhatsapp: string;
  customerNotes: string;
  serviceIds: string[];
  date: string;
  startTime: string;
}

export interface CreateBookingResult {
  success: boolean;
  booking?: Booking;
  error?: string;
}

// BACKEND INTEGRATION POINT: This function must be a transactional DB operation
// to prevent race conditions. Use SELECT FOR UPDATE or optimistic locking.
export function createBooking(payload: CreateBookingPayload): CreateBookingResult {
  const services = _services.filter(s => payload.serviceIds.includes(s.id));
  if (services.length === 0) {
    return { success: false, error: 'Nenhum serviço válido selecionado.' };
  }

  const totalDuration = services.reduce((sum, s) => sum + s.duration, 0) + _settings.breakBetweenAppointments;
  const totalPrice = services.reduce((sum, s) => sum + s.price, 0);
  const endTime = addMinutes(payload.startTime, totalDuration);

  // Re-validate availability at confirmation time (prevents double booking)
  const availableSlots = getAvailableSlots(payload.date, totalDuration);
  if (!availableSlots.includes(payload.startTime)) {
    return {
      success: false,
      error: 'Este horário acabou de ser reservado. Por favor, escolha outro horário.',
    };
  }

  const newBooking: Booking = {
    id: `bkg-${Date.now()}`,
    customerName: payload.customerName,
    customerWhatsapp: payload.customerWhatsapp,
    customerNotes: payload.customerNotes,
    serviceIds: payload.serviceIds,
    date: payload.date,
    startTime: payload.startTime,
    endTime,
    totalPrice,
    totalDuration,
    status: 'pending',
    createdAt: new Date().toISOString(),
  };

  _bookings = [..._bookings, newBooking];
  return { success: true, booking: newBooking };
}

export function updateBookingStatus(bookingId: string, status: BookingStatus): void {
  _bookings = _bookings.map(b => b.id === bookingId ? { ...b, status } : b);
}

export function updateBooking(bookingId: string, updates: Partial<Booking>): CreateBookingResult {
  const booking = _bookings.find(b => b.id === bookingId);
  if (!booking) return { success: false, error: 'Reserva não encontrada.' };

  if (updates.date || updates.startTime) {
    const newDate = updates.date || booking.date;
    const newStart = updates.startTime || booking.startTime;
    const services = _services.filter(s => booking.serviceIds.includes(s.id));
    const totalDuration = services.reduce((sum, s) => sum + s.duration, 0);

    const otherBookings = _bookings.filter(b => b.id !== bookingId && b.date === newDate && b.status !== 'cancelled');
    let startMin = timeToMinutes(newStart);
    const endMin = startMin + totalDuration;

    const conflict = otherBookings.some(b => {
      const bStart = timeToMinutes(b.startTime);
      const bEnd = timeToMinutes(b.endTime);
      return startMin < bEnd && endMin > bStart;
    });

    if (conflict) {
      return { success: false, error: 'Conflito de horário detectado. Escolha outro horário.' };
    }

    const endTime = addMinutes(newStart, totalDuration);
    _bookings = _bookings.map(b => b.id === bookingId ? { ...b, ...updates, endTime } : b);
  } else {
    _bookings = _bookings.map(b => b.id === bookingId ? { ...b, ...updates } : b);
  }

  const updated = _bookings.find(b => b.id === bookingId)!;
  return { success: true, booking: updated };
}

export function getServicesByIds(ids: string[]): Service[] {
  return _services.filter(s => ids.includes(s.id));
}

export function formatPrice(price: number): string {
  return `${price.toLocaleString('pt-MZ')} MT`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}